import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { thousandFormatter } from "~/utils/formatter";
import storageService from "~/utils/storage";
import { useTranslation } from "react-i18next";
import { useGlobalStore } from "~/store";
import { Helmet } from "react-helmet-async";
import { getTitle } from "~/utils/getTitle";
import useRequest from "~/common/hooks/useRequest";
import { API_STATE } from "~/common/constants/state";
import useAlert from "~/common/hooks/useAlert";

//Components
import NavbarAndSidebarLayout from "~/layouts/NavbarAndSidebarLayout";
import PageHeader from "~/common/components/PageHeader";
// import ModalTemplate from "~/common/components/ModalPortal/ModalTemplate";
// import TabMenu from "~/common/components/TabMenu";
// import { Case, If } from "~/common/components/If";

//Assets
import "~/assets/sass/pages/portfolio/portfolioView.scss";
import darkLogoImg from "~/assets/img/defaultImg/dark-logo-square.png";
import lightLogoImg from "~/assets/img/defaultImg/light-logo-square.png";

//Pages
import PortfolioMemo from "~/pages/portfolio/PortfolioMemo";
import CorpInfo from "./components/CorpInfo";
import InvestInfo from "./components/InvestInfo";
import MyInvestInfo from "./components/MyInvestInfo";
import MyWithdrawInfo from "./components/MyWithdrawInfo";
import InvestHistory from "./components/InvestHistory";
import CorpIntro from "./components/CorpIntro";
import ServiceIntro from "./components/ServiceIntro";
// import RelatedNews from "./components/RelatedNews";
import bubbleChartBg from "~/assets/img/bg-bubble-line.png";

const PortfolioView = () => {
	const [
		{
			sidebar: { authMap },
			theme: { theme },
		},
	] = useGlobalStore();
	const navigate = useNavigate();
	const { t, i18n } = useTranslation();

	const currentPath = authMap?.main?.portfolio?.list;

	const params = useParams();
	const { idx } = params;

	const industryRequest = useRequest("get", "vc/portfolio/industry");
	const portfolioDetailRequest = useRequest("get", `/vc/portfolio/detail/${idx}`);
	const portfolioDeleteRequest = useRequest("patch", `/vc/portfolio/delete/${idx}`);

	const [portfolio, setPortfolio] = useState({});
	const [showMemo, setShowMemo] = useState(false);
	const [category, setCategory] = useState([]);
	const [dataSet, setDataSet] = useState([]);

	const [showAuthAlert, AuthAlert] = useAlert();
	const [showDeleteAlert, DeleteAlert] = useAlert();
	const [showDeleteSuccessAlert, DeleteSuccessAlert] = useAlert();

	// const [IrModal, setIrModal] = useState(false);
	// const [currentTab, setCurrentTab] = useState("basicInfo");
	// const tabMenuList = [
	// 	{ id: "basicInfo", title: "기본정보" },
	// 	{ id: "history", title: "주요연혁" },
	// 	{ id: "member", title: "주요인력" },
	// 	{ id: "shareholder", title: "주주현황" },
	// 	{ id: "finance", title: "재무정보" },
	// 	{ id: "product", title: "제품/기술/시장" },
	// 	{ id: "outcome", title: "성과 및 계획" },
	// ];

	const isValidUrl = (url) => {
		let regex = /(http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-\/]))?/;
		return regex.test(url);
	};

	const handleClickHomepage = (url) => {
		if (!isValidUrl(url)) return;
		window.open(url, "_blank");
	};

	useEffect(() => {
		industryRequest.singleCall();
		portfolioDetailRequest.singleCall();

		const closedMemo = storageService.get("memo", "closedMemo", "local") || {};
		if (!closedMemo[`idx${idx}`]) {
			setShowMemo(true);
		}
	}, []);

	useEffect(() => {
		switch (portfolioDetailRequest.state) {
			case API_STATE.done:
				const { portfolio } = portfolioDetailRequest.response?.data;
				setPortfolio(portfolio);
				const values = [];
				const years = [];
				const data = [];
				const r = [0, 800, 270, 140, 83, 57, 39, 30, 23, 18, 15];
				if (portfolio?.StartupInvest?.length > 0) {
					portfolio?.StartupInvest?.forEach((h) => {
						const year = h.date.substring(0, 4);
						if (years.findIndex((y) => y === year) < 0) {
							years.push(year);
						}
					});
					years.sort((a, b) => {
						return parseInt(a) - parseInt(b);
					});
					years.splice(0, years.length - 10);
					years.forEach((y, y_idx) => {
						values.push({
							label: y,
							x: (y_idx + 1) * years.length * r[years.length],
						});
					});
					setCategory(values);
					values.forEach((v, v_idx) => {
						let investSum = 0;
						portfolio?.StartupInvest?.forEach((h) => {
							if (h.date.includes(v.label)) {
								investSum += Number(h.value);
							}
						});
						data.push({
							x: ((v_idx + 1) * years.length * r[years.length]).toString(),
							y: "50",
							z: investSum.toString(),
							name: `${thousandFormatter(investSum)}${
								i18n.language === "ko" ? "억" : "million"
							}`,
							color: v_idx + 1 === values.length ? "#6B44FF" : "#c9ced6",
						});
					});
					setDataSet(data);
				}
				break;
			default:
				break;
		}
	}, [portfolioDetailRequest.state, portfolioDetailRequest.response, i18n.language]);

	useEffect(() => {
		switch (portfolioDeleteRequest.state) {
			case API_STATE.done:
				showDeleteSuccessAlert().then(() => {
					navigate("/portfolio/list");
				});
				break;
			default:
				break;
		}
	}, [portfolioDeleteRequest.state, portfolioDeleteRequest.response]);

	const headerButtons = [
		<button
			type="button"
			className="btn__solid"
			color="red"
			onClick={() => {
				if (currentPath !== "edit") {
					showAuthAlert();
					return;
				}
				showDeleteAlert().then(() => {
					portfolioDeleteRequest.call();
				});
			}}
		>
			{t("pages.portfolio.PortfolioView.remove")}
		</button>,
		<button
			type="button"
			className="btn__solid"
			color="primary"
			onClick={() => {
				if (currentPath !== "edit") {
					showAuthAlert();
					return;
				}
				navigate(`/portfolio/edit/${idx}`);
			}}
		>
			{t("pages.portfolio.PortfolioView.mod")}
		</button>,
	];

	const config = {
		type: "bubble",
		dataFormat: "json",
		width: "100%",
		height: "266",
		containerBackgroundOpacity: "0",
		dataSource: {
			chart: {
				xAxisMinValue: "0",
				yAxisMinValue: "0",
				xAxisMaxValue: "1700",
				yAxisMaxValue: "135",
				xAxisValuesStep: "1",
				showYAxisValues: "0",
				showXAxisLine: "0",
				bgImage: bubbleChartBg,
				bgAlpha: "0",
				bgImageDisplayMode: "center",
				divLineAlpha: "0",
				plotFillAlpha: "80",
				plotFillHoverColor: "#603aff",
				showTooltip: false,
				theme: "fusion",
				bubbleScale: 3,
				showValues: "1",
			},
			categories: [
				{
					category: category,
				},
			],
		},
	};
	const dataset = {
		dataset: [
			{
				color: "#c9ced6",
				plotFillAlpha: 80,
				data: dataSet,
			},
		],
	};

	return (
		<>
			<Helmet>
				<title>{getTitle(`포트폴리오`)}</title>
			</Helmet>
			<NavbarAndSidebarLayout menu={["portfolio", "view"]} type={"main"}>
				<PageHeader buttonList={headerButtons} />
				<div className="c__card card-portfolio-view">
					<div className="portfolio-common-wrap">
						<section className="basic-info-section">
							<ul className="basic-info-wrap">
								<li>
									<div className="logo-img">
										<img
											src={
												portfolio.logoSqu ||
												(theme === "dark" ? darkLogoImg : lightLogoImg)
											}
											alt={t("pages.portfolio.PortfolioView.logo")}
										/>
									</div>
									<p className="company-name">
										{portfolio.name ||
											t("pages.portfolio.PortfolioView.companyName")}
									</p>
									<p
										className="company-url"
										onClick={() => handleClickHomepage(portfolio.homepage)}
									>
										{portfolio.homepage ||
											t("pages.portfolio.PortfolioView.hp")}
									</p>
									{/* <p className="company-url"><a{portfolio.homepage || "홈페이지 주소"}</p> */}
								</li>
								<CorpInfo portfolio={portfolio} />
								<InvestInfo portfolio={portfolio} />
								{portfolio?.VcInvest && portfolio?.VcInvest.length > 0 && (
									<MyInvestInfo portfolio={portfolio} />
								)}
								{portfolio?.VcWithdraw && portfolio?.VcWithdraw.length > 0 && (
									<MyWithdrawInfo portfolio={portfolio} />
								)}
							</ul>
						</section>
						<section className="detail-info-section">
							<ul className="detail-info-wrap">
								<InvestHistory
									portfolio={portfolio}
									config={config}
									dataset={dataset}
								/>
								<CorpIntro portfolio={portfolio} />
								<ServiceIntro portfolio={portfolio} />
								{/*<RelatedNews portfolio={portfolio} />*/}
							</ul>
						</section>
					</div>
				</div>
			</NavbarAndSidebarLayout>

			{/* 포트폴리오 메모 */}
			<PortfolioMemo showMemo={showMemo} />

			<DeleteAlert
				type="error"
				message={
					<>
						<h3>{t("pages.portfolio.PortfolioView.wouldRemove")}</h3>
					</>
				}
				reject={t("pages.portfolio.PortfolioView.nein")}
				confirm={t("pages.portfolio.PortfolioView.yes")}
				layout="horiz"
				height={230}
			/>

			<DeleteSuccessAlert
				type="success"
				message={t("pages.portfolio.PortfolioView.sucessRemove")}
				confirm={t("pages.portfolio.PortfolioView.check")}
			/>

			<AuthAlert
				message={
					<>
						권한이 없습니다. <br />
						관리자에게 문의하세요.
					</>
				}
				type="warning"
				confirm="확인"
			/>
		</>
	);
};

export default PortfolioView;
